import type { PlaybackIntent } from './lifecycle';

type MediaAction = 'play' | 'pause' | 'seekto' | 'seekbackward' | 'seekforward';

export interface MediaSessionLike {
  metadata: MediaMetadata | null;
  playbackState: MediaSessionPlaybackState;
  setActionHandler(action: MediaAction, handler: MediaSessionActionHandler | null): void;
  setPositionState?(state?: MediaPositionState): void;
}

/** Media Session describes state; it does not grant background execution. */
export class MediaSessionController {
  private active = false;

  constructor(private readonly session: MediaSessionLike) {}

  start(metadata: MediaMetadata, onPlay: () => void, onPause: () => void): void {
    this.active = true;
    this.session.metadata = metadata;
    this.setHandler('play', onPlay);
    this.setHandler('pause', onPause);
    // A seek position cannot recreate synthesised silence which has already played.
    this.setHandler('seekto', null);
    this.setHandler('seekbackward', null);
    this.setHandler('seekforward', null);
  }

  sync(intent: PlaybackIntent, rendererPlaying: boolean, position: number, duration: number): void {
    if (!this.active) return;
    this.session.playbackState = intent === 'playing' && rendererPlaying ? 'playing' : 'paused';
    if (!this.session.setPositionState || duration <= 0) return;
    try {
      this.session.setPositionState({
        duration,
        playbackRate: 1,
        position: Math.min(duration, Math.max(0, position)),
      });
    } catch {
      // Safari exposed Media Session before accepting every valid position-state call.
      // Controls remain useful even when that optional readout is absent.
    }
  }

  stop(): void {
    if (!this.active) return;
    for (const action of ['play', 'pause', 'seekto', 'seekbackward', 'seekforward'] as const) {
      this.setHandler(action, null);
    }
    this.session.metadata = null;
    this.session.playbackState = 'none';
    try { this.session.setPositionState?.(); } catch { /* optional state was never accepted */ }
    this.active = false;
  }

  private setHandler(action: MediaAction, handler: (() => void) | null): void {
    try {
      this.session.setActionHandler(action, handler);
    } catch {
      // Implementations are allowed to reject actions they do not expose. One unsupported
      // seek action must not prevent play and pause from representing the sit truthfully.
    }
  }
}
